import React, { useEffect, useState } from 'react';
import { Modal, Input, InputNumber, Select, Empty, Spin, Tag, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import config from '../../config';
import { NdoeImage } from '../../components/ndoe';
import './cataloguePicker.css';

const POINTURES = Array.from({ length: 13 }, (_, i) => 34 + i);

/*
  LE PRIX AFFICHÉ EST CELUI QU'ON PAIERA — corrigé le 31/08/2026.

  Ce sélecteur montrait `v.prix`, c'est-à-dire le prix de BASE, même sur une
  paire en promotion : le chiffre lu pendant le choix n'était donc pas celui
  retenu à la commande. On choisissait une paire sur un montant faux.

  La donnée était pourtant déjà là. `/api/catalogue/stock` enrichit chaque
  variante avec `prix_base`, `prix_promo` et `promo_libelle` (services/
  promoPricing.service.js) — champs ADDITIFS, présents seulement quand une
  promotion s'applique vraiment ce jour-là. Il n'y avait qu'à les lire.

  Présentation reprise telle quelle de l'écran Promotions (Promotions.jsx) :
  prix de base barré, puis le prix promo. On ne réinvente pas une façon
  d'écrire une remise dans le quatrième écran qui en montre une.
*/
const PrixVariante = ({ v, suffixe = '' }) => {
  if (!v || v.prix == null) return null;
  if (v.prix_promo == null) return <span className="cp-prix">{v.prix} ${suffixe}</span>;
  return (
    <span className="cp-prix cp-prix--promo" title={v.promo_libelle || 'Promotion en cours'}>
      <s>{v.prix_base ?? v.prix} $</s>
      {' '}<b>{v.prix_promo} $</b>{suffixe}
    </span>
  );
};

// Popup de recherche visuelle d'une paire (vignettes + filtres + référence).
// À la sélection, renvoie la VARIANTE vérité-stock complète via onSelect.
//
// Options (toutes rétro-compatibles — les 5 usages historiques sont inchangés) :
//  - avecPrix       : affiche le prix sur chaque carte (à gater par canSeeFinance
//                     côté appelant) ;
//  - avecQuantite   : au clic, ouvre une étape quantité (bornée au disponible si
//                     bloquerRupture) et renvoie onSelect({ ...variante, quantite }) ;
//  - bloquerRupture : les articles à 0 disponible sont grisés et non cliquables ;
//  - compteur       : nombre affiché dans le titre (« N article(s) ajouté(s) »)
//                     pour le mode multi-ajout où l'appelant laisse le modal ouvert.
const CataloguePicker = ({
  open, onClose, onSelect, refLists,
  avecPrix = false, avecQuantite = false, bloquerRupture = false, compteur = null,
}) => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const [filters, setFilters] = useState({ id_categorie: null, id_marque: null, id_couleur: null, taille: null });
  const [q, setQ] = useState('');
  // TRI par défaut = le plus de stock disponible d'abord. L'ordre alphabétique
  // faisait remonter en tête des paires en RUPTURE (les « B… » du catalogue),
  // alors que ce sélecteur sert à choisir une paire qu'on peut réellement vendre.
  const [tri, setTri] = useState('stock_desc');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  // Étape quantité (mode avecQuantite) : { variante, qte }
  const [etapeQte, setEtapeQte] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const p = new URLSearchParams({ limit: '120' });
        if (q.trim()) p.set('q', q.trim());
        if (tri) p.set('sort', tri);
        Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, String(v)); });
        const { data } = await axios.get(`${DOMAIN}/api/catalogue/stock?${p.toString()}`);
        if (alive) setList(Array.isArray(data) ? data : (data?.variantes || []));
      } catch (e) { console.log(e); } finally { if (alive) setLoading(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [open, q, filters, tri, DOMAIN]);

  useEffect(() => { if (!open) setEtapeQte(null); }, [open]);

  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v || null }));

  const choisir = (v) => {
    if (bloquerRupture && !(v.disponible > 0)) return;
    if (avecQuantite) {
      setEtapeQte({ variante: v, qte: 1 });
      return;
    }
    onSelect(v);
  };

  const validerQte = () => {
    if (!etapeQte) return;
    const max = bloquerRupture ? Number(etapeQte.variante.disponible) : 9999;
    const qte = Math.max(1, Math.min(Number(etapeQte.qte) || 1, max));
    onSelect({ ...etapeQte.variante, quantite: qte });
    setEtapeQte(null);
  };

  const titre = compteur !== null && compteur > 0
    ? `Rechercher une paire — ${compteur} article(s) ajouté(s)`
    : 'Rechercher une paire dans le catalogue';

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={940} className="cp-modal" title={titre}>
      <div className="cp-filters">
        <Input allowClear prefix={<SearchOutlined />} placeholder="Nom ou référence (ex. ESC-ZAR)…" value={q} onChange={(e) => setQ(e.target.value)} className="cp-search" />
        <Select allowClear placeholder="Catégorie" options={refLists.categories} value={filters.id_categorie} onChange={setF('id_categorie')} showSearch optionFilterProp="label" className="cp-f" />
        <Select allowClear placeholder="Marque" options={refLists.marques} value={filters.id_marque} onChange={setF('id_marque')} showSearch optionFilterProp="label" className="cp-f" />
        <Select allowClear placeholder="Couleur" options={refLists.couleurs} value={filters.id_couleur} onChange={setF('id_couleur')} showSearch optionFilterProp="label" className="cp-f" />
        <Select allowClear placeholder="Pointure" options={POINTURES.map((p) => ({ value: p, label: `P${p}` }))} value={filters.taille} onChange={setF('taille')} className="cp-f cp-f-pt" />
        {/* TRI — mêmes clés que le catalogue de partage (liste blanche serveur),
            enrichies du tri par stock demandé pour choisir vite ce qu'on a en
            quantité. Le stock affiché est le DISPONIBLE (hors réservé/en tournée). */}
        <Select value={tri} onChange={setTri} className="cp-f cp-f-tri" title="Trier le catalogue"
          options={[
            { value: 'stock_desc', label: 'Plus de stock d’abord' },
            { value: 'stock_asc', label: 'Moins de stock d’abord' },
            { value: 'nom_asc', label: 'Nom : A→Z' },
            { value: 'sales_desc', label: 'Meilleures ventes (90 j)' },
            { value: 'last_sale', label: 'Dernières ventes' },
            { value: 'last_receipt', label: 'Derniers arrivages' },
            { value: 'dormant_stock', label: 'Dort en stock (0 vente 90 j)' },
            { value: 'never_sold', label: 'Jamais vendu' },
            { value: 'restock', label: 'À réassortir (se vend, stock bas)' },
            { value: 'prix_desc', label: 'Prix décroissant' },
            { value: 'prix_asc', label: 'Prix croissant' },
          ]} />
      </div>
      <Spin spinning={loading}>
        <div className="cp-grid">
          {list.map((v) => {
            const rupture = !(v.disponible > 0);
            const inerte = bloquerRupture && rupture;
            return (
              <button
                type="button"
                key={v.id_varianteProduit}
                className={`cp-card ${inerte ? 'cp-card--off' : ''}`}
                onClick={() => choisir(v)}
                disabled={inerte}
                title={inerte ? 'En rupture — non sélectionnable' : `Choisir ${v.code_article || ''}`}
              >
                <div className="cp-thumb"><NdoeImage src={v.img} alt={v.nom_produit || 'Article'} size="lg" sizes="200px" className='img' /></div>
                <div className="cp-ref">{v.code_article || '—'}</div>
                <div className="cp-nom">{v.nom_produit}</div>
                <div className="cp-sub">{v.couleur || '—'} · P{v.taille ?? '?'}</div>
                <div className="cp-bas">
                  {avecPrix && <PrixVariante v={v} />}
                  <Tag className={`cp-dispo ${rupture ? 'ko' : 'ok'}`}>{rupture ? 'Rupture' : `${v.disponible} dispo`}</Tag>
                </div>
              </button>
            );
          })}
          {!loading && !list.length && <div className="cp-empty"><Empty description="Aucune paire pour ces critères" /></div>}
        </div>
      </Spin>

      {/* Étape quantité — mode avecQuantite : bornée au disponible. */}
      <Modal
        open={!!etapeQte}
        onCancel={() => setEtapeQte(null)}
        footer={null}
        width={380}
        centered
        title="Quantité"
        destroyOnClose
      >
        {etapeQte && (
          <div className="cp-qte">
            <div className="cp-qte__art">
              <NdoeImage src={etapeQte.variante.img} alt="" size="md" preview={false} />
              <div>
                <b>{etapeQte.variante.code_article || '—'}</b>
                <div className="cp-qte__nom">{etapeQte.variante.nom_produit} · P{etapeQte.variante.taille ?? '?'}</div>
                {avecPrix && <div><PrixVariante v={etapeQte.variante} suffixe=" / paire" /></div>}
              </div>
            </div>
            <div className="cp-qte__ligne">
              <span>Quantité{bloquerRupture ? ` (max ${etapeQte.variante.disponible})` : ''}</span>
              <InputNumber inputMode="decimal"
                min={1}
                max={bloquerRupture ? Number(etapeQte.variante.disponible) : 9999}
                value={etapeQte.qte}
                onChange={(v) => setEtapeQte((s) => s && { ...s, qte: v })}
                autoFocus
              />
            </div>
            <Button type="primary" block onClick={validerQte}
              style={{ background: '#071c3d', borderColor: '#071c3d', height: 42 }}>
              Ajouter à la commande
            </Button>
          </div>
        )}
      </Modal>
    </Modal>
  );
};

export default CataloguePicker;
